import { useEffect, type ReactNode } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import {
  getArticleContent,
  getPostBySlug,
  posts,
  absoluteUrl,
  postSrcSet,
  type ArticleBlock,
} from "@/lib/blog-posts";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Byline } from "@/components/Byline";
import { RelatedArticles, RelatedServices } from "@/components/RelatedLinks";

const relatedServiceSlugs = (slug: string) =>
  slug.startsWith("how-to-dispose-of-branded-stock")
    ? ["product-destruction", "textile-recycling", "one-off-shredding"]
    : ["textile-recycling", "product-destruction", "one-off-shredding"];
import { breadcrumbScript, ORG_ID, WEBSITE_ID } from "@/lib/seo";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = getPostBySlug(params.slug);
    const article = getArticleContent(params.slug);
    if (!post) throw notFound();
    return { post, article };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Article | Preserve Earth" }] };
    const { post } = loaderData;
    const url = `https://preserveearth.co.uk/blog/${params.slug}`;
    const seoTitle = post.seoTitle ?? `${post.title} | Preserve Earth`;
    return {
      meta: [
        { title: seoTitle },
        { name: "description", content: post.excerpt },
        { property: "og:title", content: seoTitle },
        { name: "twitter:title", content: seoTitle },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:description", content: post.excerpt },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: absoluteUrl(post.image) },
        { property: "article:published_time", content: post.datePublished },
        { property: "article:modified_time", content: post.dateModified },
        { property: "article:section", content: post.category },
        { name: "twitter:image", content: absoluteUrl(post.image) },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt,
            image: absoluteUrl(post.image),
            datePublished: post.datePublished,
            dateModified: post.dateModified,
            articleSection: post.category,
            author: { "@type": "Organization", name: "Preserve Earth team", url: "https://preserveearth.co.uk/" },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            publisher: { "@id": ORG_ID },
            isPartOf: { "@id": WEBSITE_ID },
          }),
        },
        breadcrumbScript([
          { name: "Blog", path: "/blog" },
          { name: post.title, path: `/blog/${params.slug}` },
        ]),
      ],
    };
  },
  component: BlogPostPage,
  notFoundComponent: () => (
    <div className="container-page py-32 text-center">
      <p className="text-xs uppercase tracking-[0.25em] text-moss">Not found</p>
      <h1 className="mt-6 font-serif text-4xl text-charcoal">
        That article doesn't exist.
      </h1>
      <Link
        to="/blog"
        className="mt-8 inline-block text-sm text-moss underline underline-offset-4"
      >
        ← Back to all articles
      </Link>
    </div>
  ),
});

function renderInline(block: ArticleBlock): ReactNode {
  if (!block.links?.length) return block.v;
  const out: ReactNode[] = [];
  let rest = block.v;
  let key = 0;
  // Links are applied in text order; each wraps the first remaining match.
  const ordered = [...block.links].sort(
    (a, b) => block.v.indexOf(a.text) - block.v.indexOf(b.text),
  );
  for (const l of ordered) {
    const idx = rest.indexOf(l.text);
    if (idx === -1 || !(l.href.startsWith("/") || l.href.startsWith("https://") || l.href.startsWith("mailto:"))) continue;
    if (idx > 0) out.push(rest.slice(0, idx));
    out.push(
      <a
        key={key++}
        href={l.href}
        className="underline decoration-moss/40 underline-offset-4 transition-colors hover:text-moss"
      >
        {l.text}
      </a>,
    );
    rest = rest.slice(idx + l.text.length);
  }
  if (rest) out.push(rest);
  return out;
}

function renderBlock(block: ArticleBlock, i: number) {
  switch (block.t) {
    case "h1":
      return null; // shown in the hero
    case "h2":
      return (
        <h2
          key={i}
          className="mt-14 font-serif text-3xl leading-tight text-charcoal lg:text-4xl"
        >
          {renderInline(block)}
        </h2>
      );
    case "h3":
      return (
        <h3
          key={i}
          className="mt-10 font-serif text-xl leading-snug text-moss"
        >
          {renderInline(block)}
        </h3>
      );
    default:
      return (
        <p
          key={i}
          className="mt-5 text-pretty text-base leading-[1.8] text-charcoal/80"
        >
          {renderInline(block)}
        </p>
      );
  }
}

function BlogPostPage() {
  const { post, article } = Route.useLoaderData();
  const blocks = article?.blocks ?? [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [post.slug]);


  // Same-category posts first, then the remaining posts in list order.
  const others = posts.filter((p) => p.slug !== post.slug);
  const related = [...others.filter((p) => p.category === post.category), ...others.filter((p) => p.category !== post.category)].slice(0, 3);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader
        links={[
          { label: "Home", href: "/" },
          { label: "About", href: "/about" },
          { label: "Services", href: "/services" },
          { label: "Blog", href: "/blog" },
        ]}
        enquireHref="/#enquire"
      />

      <main>
        {/* Article header */}
        <article>
          <header className="container-page pt-16 pb-10">
            <Breadcrumbs crumbs={[{ name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }]} />

            <p className="mb-6 text-xs font-medium uppercase tracking-[0.25em] text-moss">
              {post.category} · {post.readMinutes} min read
            </p>
            <h1 className="max-w-[28ch] text-balance font-serif text-4xl leading-[1.05] text-charcoal md:text-5xl lg:text-6xl">
              {post.title}
            </h1>
            <p className="mt-8 max-w-[60ch] text-pretty text-lg leading-relaxed text-charcoal/70">
              {post.excerpt}
            </p>
            <Byline published={post.datePublished} modified={post.dateModified} />
          </header>

          {/* Hero image */}
          <div className="container-page">
            <figure className="overflow-hidden rounded-md outline outline-1 -outline-offset-1 outline-charcoal/10">
              <img
                src={post.image}
                srcSet={postSrcSet(post)}
                sizes="(min-width: 1440px) 1344px, 100vw"
                alt={post.title}
                width={1440}
                height={756}
                loading="eager"
                fetchPriority="high"
                className="aspect-[16/9] w-full object-cover"
              />
            </figure>
          </div>

          {/* Article body */}
          <div className="container-page py-20">
            <div className="grid gap-16 lg:grid-cols-12">
              {/* Side rail */}
              <aside className="lg:col-span-3">
                <div className="sticky top-24 space-y-6 border-l border-moss/30 pl-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">
                    The Ledger
                  </p>
                  <p className="text-sm leading-relaxed text-charcoal/70">
                    Field notes on compliant, documented disposal for UK
                    businesses.
                  </p>
                  <Link
                    to="/blog"
                    className="block text-xs font-medium uppercase tracking-[0.2em] text-moss underline decoration-moss/30 underline-offset-4"
                  >
                    ← All articles
                  </Link>
                </div>
              </aside>

              <div className="lg:col-span-9 lg:max-w-[68ch]">
                {blocks.length > 0 ? (
                  blocks.map(renderBlock)
                ) : (
                  <p className="text-base leading-[1.8] text-charcoal/80">
                    {post.excerpt}
                  </p>
                )}

                {/* Inline CTA */}
                <aside className="mt-20 border-y border-charcoal/15 bg-sage-light/40 px-8 py-10">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">
                    Need a compliant route?
                  </p>
                  <h2 className="mt-3 font-serif text-2xl text-charcoal">
                    Arrange a secure collection across the United Kingdom.
                  </h2>
                  <p className="mt-3 max-w-[58ch] text-sm leading-relaxed text-charcoal/70">
                    Site assessments, secure handling and full audit trails are
                    arranged for businesses across the UK on request.
                  </p>
                  <a
                    href="/#enquire"
                    className="mt-6 inline-flex items-center gap-3 rounded-sm bg-moss px-5 py-3 text-sm font-medium text-cream transition-colors hover:bg-charcoal"
                  >
                    Request a collection →
                  </a>
                </aside>
              </div>
            </div>
          </div>
        </article>

        {/* Related services + articles */}
        <RelatedServices slugs={relatedServiceSlugs(post.slug)} className="bg-cream" />
        <RelatedArticles slugs={related.map((r) => r.slug)} className="bg-cream" />
      </main>

      <SiteFooter />
    </div>
  );
}
